import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  TransactionsSortField,
  TransactionFilters,
  TransactionSort,
} from "@/api/transactions/types";

interface TransactionsFilterContextProps {
  filters: TransactionFilters;
  setFilters: (filters: TransactionFilters) => void;
  search: string;
  setSearch: (value: string) => void;
  page: number;
  setPage: (page: number) => void;
  sort: TransactionSort;
  handleSortChange: (sortField: TransactionsSortField) => void;
}

const TransactionsFilterContext = createContext<
  TransactionsFilterContextProps | undefined
>(undefined);

export const TransactionsFilterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<TransactionSort>({
    sortField: "timestamp",
    sortType: "desc" as "asc" | "desc",
  });

  const handleSortChange = (sortField: TransactionsSortField) => {
    setSort((prevSort) => {
      if (prevSort.sortField === sortField) {
        return {
          ...prevSort,
          sortType: prevSort.sortType === "asc" ? "desc" : "asc",
        };
      }
      return { sortField, sortType: "desc" };
    });
  };

  useEffect(() => {
    setPage(1);
  }, [filters, search, sort]);

  return (
    <TransactionsFilterContext.Provider
      value={{
        filters,
        setFilters,
        search,
        setSearch,
        page,
        setPage,
        sort,
        handleSortChange,
      }}
    >
      {children}
    </TransactionsFilterContext.Provider>
  );
};

export const useTransactionsFilterContext =
  (): TransactionsFilterContextProps => {
    const context = useContext(TransactionsFilterContext);
    if (!context) {
      throw new Error(
        "useTransactionsContext must be used within a TransactionsProvider"
      );
    }
    return context;
  };
